import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listReviewsForModeration, setReviewModeration } from "@/lib/review-moderation.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { AiReviewCheck } from "@/components/AiReviewCheck";

export const Route = createFileRoute("/_authenticated/admin/review-moderation")({
  head: () => ({ meta: [{ title: "Review moderation — Admin" }] }),
  component: ReviewModerationPage,
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

type Review = {
  id: string; rating: number; comment: string | null;
  bartender_name: string | null; venue_name: string | null;
  moderation_status: string; is_hidden: boolean; hidden_reason: string | null;
  flagged_at: string | null; flagged_by: string | null; flagged_by_name: string | null;
  created_at: string;
};

type Tab = "queue" | "history" | "all";

function statusTone(s: string, hidden: boolean) {
  if (hidden) return "bg-destructive/10 text-destructive border-destructive/30";
  if (s === "flagged") return "bg-amber-500/10 text-amber-600 border-amber-500/30";
  if (s === "removed") return "bg-destructive/10 text-destructive border-destructive/30";
  return "bg-muted text-muted-foreground border-border";
}

function ReviewModerationPage() {
  const load = useServerFn(listReviewsForModeration);
  const mod = useServerFn(setReviewModeration);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("queue");

  const refresh = async () => {
    setLoading(true);
    const r = await load();
    if ("error" in r) toast.error(r.error); else setReviews(r.reviews as Review[]);
    setLoading(false);
  };
  useEffect(() => { void refresh(); }, []);

  const counts = useMemo(() => {
    const queue = reviews.filter((r) => r.moderation_status === "flagged" && !r.is_hidden).length;
    const history = reviews.filter((r) => r.is_hidden || ["hidden", "removed"].includes(r.moderation_status) || r.flagged_at).length;
    return { queue, history, all: reviews.length };
  }, [reviews]);

  const filtered = useMemo(() => {
    if (tab === "queue") return reviews.filter((r) => r.moderation_status === "flagged" && !r.is_hidden);
    if (tab === "history") return reviews.filter((r) => r.is_hidden || ["hidden", "removed"].includes(r.moderation_status) || r.flagged_at);
    return reviews;
  }, [reviews, tab]);

  const act = async (id: string, action: "hide" | "unhide" | "flag") => {
    const reason = action === "hide" ? prompt("Reason for hiding (visible to admins only)?") ?? undefined : undefined;
    if (action === "hide" && !reason) return;
    try {
      const res = await mod({ data: { id, action, reason } });
      if ("error" in res) throw new Error(res.error);
      toast.success("Updated.");
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Could not update."); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Admin · Safety</p>
        <h1 className="text-3xl font-light">Review moderation</h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">
          Review reports from the public profiles, approve or hide content, and track every action you've taken.
        </p>

        <div className="mt-6 flex border-b border-border text-xs tracking-wide-luxury uppercase">
          {([
            ["queue", `Queue · ${counts.queue}`],
            ["history", `History · ${counts.history}`],
            ["all", `All · ${counts.all}`],
          ] as Array<[Tab, string]>).map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-4 py-2 -mb-px border-b-2 ${tab === id ? "border-accent text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
        : filtered.length === 0 ? <p className="mt-6 text-sm text-muted-foreground">
            {tab === "queue" ? "Nothing to review — all reports cleared." :
             tab === "history" ? "No moderation actions yet." : "No reviews yet."}
          </p>
        : (
          <ul className="mt-6 divide-y divide-border border border-border">
            {filtered.map((r) => (
              <li key={r.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm">
                      {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                      <span className="text-xs text-muted-foreground"> · {new Date(r.created_at).toLocaleDateString("en-GB")}</span>
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">{r.venue_name ?? "—"} → {r.bartender_name ?? "—"}</p>
                    {r.comment && <p className="mt-2 text-sm whitespace-pre-wrap">{r.comment}</p>}
                    <AiReviewCheck reviewId={r.id} />
                    {r.flagged_at && (
                      <p className="mt-2 text-[11px] text-amber-700">
                        Flagged {new Date(r.flagged_at).toLocaleString("en-GB")}
                        {r.flagged_by_name ? ` by ${r.flagged_by_name}` : ""}
                        {r.hidden_reason ? ` — “${r.hidden_reason}”` : ""}
                      </p>
                    )}
                    {r.is_hidden && !r.flagged_at && (
                      <p className="mt-2 text-xs text-destructive">Hidden: {r.hidden_reason ?? "no reason recorded"}</p>
                    )}
                  </div>
                  <span className={`shrink-0 text-[10px] uppercase tracking-wide-luxury border px-2 py-1 ${statusTone(r.moderation_status, r.is_hidden)}`}>
                    {r.is_hidden ? "hidden" : r.moderation_status}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {!r.is_hidden && <Button size="sm" variant="outline" onClick={() => act(r.id, "hide")}>Hide</Button>}
                  {r.is_hidden && <Button size="sm" variant="outline" onClick={() => act(r.id, "unhide")}>Restore</Button>}
                  {!r.is_hidden && r.moderation_status === "flagged" && (
                    <Button size="sm" variant="ghost" onClick={() => act(r.id, "unhide")}>Approve (clear flag)</Button>
                  )}
                  {!r.is_hidden && r.moderation_status !== "flagged" && (
                    <Button size="sm" variant="ghost" onClick={() => act(r.id, "flag")}>Flag</Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
